import { SessionSearchScope, SessionStatus } from '@vance/generated';
import { brainFetch } from './restClient';
/**
 * GET /brain/{tenant}/sessions — owner-scoped list of the current user's
 * sessions. By default excludes ARCHIVED and CLOSED; flip {@code includeArchived}
 * or pass an explicit {@code status} set to override.
 */
export async function listSessions(options = {}) {
    const params = new URLSearchParams();
    if (options.projectId)
        params.set('projectId', options.projectId);
    if (options.status && options.status.length > 0) {
        // Generated TS enums are numeric (Java enum order). Brain expects
        // the enum name on the wire, same as Jackson.
        params.set('status', options.status.map((s) => SessionStatus[s]).join(','));
    }
    if (options.includeArchived)
        params.set('includeArchived', 'true');
    if (options.tag)
        params.set('tag', options.tag);
    const qs = params.toString();
    return brainFetch('GET', `sessions${qs ? `?${qs}` : ''}`);
}
/**
 * GET /brain/{tenant}/sessions/search — owner-scoped search. Default
 * scope BOTH (metadata + chat content); default includeArchived=true so
 * the dialog can find archived sessions.
 */
export async function searchSessions(options) {
    const params = new URLSearchParams();
    params.set('q', options.q);
    if (options.scope !== undefined) {
        params.set('scope', SessionSearchScope[options.scope]);
    }
    if (options.includeArchived !== undefined) {
        params.set('includeArchived', String(options.includeArchived));
    }
    if (options.limit)
        params.set('limit', String(options.limit));
    return brainFetch('GET', `sessions/search?${params.toString()}`);
}
/**
 * PATCH /brain/{tenant}/sessions/{id}/metadata — partial update of
 * title/icon/color/tags/pinned. Returns the post-patch metadata.
 */
export async function patchSessionMetadata(sessionId, patch) {
    return brainFetch('PATCH', `sessions/${encodeURIComponent(sessionId)}/metadata`, { body: patch });
}
/** POST /brain/{tenant}/sessions/{id}/archive — idempotent. */
export async function archiveSession(sessionId) {
    await brainFetch('POST', `sessions/${encodeURIComponent(sessionId)}/archive`);
}
/** POST /brain/{tenant}/sessions/{id}/reactivate — only valid on ARCHIVED. */
export async function reactivateSession(sessionId) {
    await brainFetch('POST', `sessions/${encodeURIComponent(sessionId)}/reactivate`);
}
/** DELETE /brain/{tenant}/sessions/{id} — hard delete, no undo. */
export async function deleteSession(sessionId) {
    await brainFetch('DELETE', `sessions/${encodeURIComponent(sessionId)}`);
}
/**
 * POST /brain/{tenant}/sessions/{id}/compact — manually compact the
 * session's chat memory now (fold older turns into a summary). Owner-only.
 * A no-op (nothing to compact) is a normal result carried in {@code reason},
 * not an error; {@code deferred} means it was queued behind a running turn.
 */
export async function compactSession(sessionId) {
    return brainFetch('POST', `sessions/${encodeURIComponent(sessionId)}/compact`);
}
/**
 * POST /brain/{tenant}/sessions/{id}/duplicate — duplicates the session
 * together with its chat memory (chat process + history + memories) into
 * a fresh, resumable copy in the same project. Returns the new session's
 * business id + resolved title. Owner-only.
 */
export async function duplicateSession(sessionId, title) {
    return brainFetch('POST', `sessions/${encodeURIComponent(sessionId)}/duplicate`, { body: title != null ? { title } : {} });
}
/**
 * POST /brain/{tenant}/sessions/{id}/move — move the session into another
 * project of the same tenant, in place (the {@code sessionId} is kept).
 * Deliberately lossy: the project-bound memory does not travel and is
 * dropped, and the session leaves its source-project group. Only valid
 * while no think-process is running. Owner-only; requires CREATE on the
 * target project. See {@code planning/session-move.md}.
 */
export async function moveSession(sessionId, targetProjectId) {
    return brainFetch('POST', `sessions/${encodeURIComponent(sessionId)}/move`, { body: { targetProjectId } });
}
/**
 * GET /brain/{tenant}/sessions/{id}/messages — chat history. Used by the
 * search-result preview and the chat editor; works on ARCHIVED sessions
 * too (their chat history persists across archive).
 */
export async function getSessionMessages(sessionId, limit) {
    const qs = limit ? `?limit=${limit}` : '';
    return brainFetch('GET', `sessions/${encodeURIComponent(sessionId)}/messages${qs}`);
}
/**
 * GET .../messages?includeRemoved=true — the Modify/Crop view: the logical
 * conversation including already-removed messages (so they can be restored),
 * minus interim working-log noise. Owner-only. Removed messages carry
 * {@code meta.kind === 'removed'}.
 */
export async function getCropMessages(sessionId) {
    return brainFetch('GET', `sessions/${encodeURIComponent(sessionId)}/messages?includeRemoved=true`);
}
/**
 * PATCH .../messages/crop — remove and/or restore messages from the
 * session's chat memory. Returns the fresh crop list (same shape as
 * {@link getCropMessages}). Owner-only.
 */
export async function cropSession(sessionId, change) {
    return brainFetch('PATCH', `sessions/${encodeURIComponent(sessionId)}/messages/crop`, { body: { remove: change.remove ?? [], restore: change.restore ?? [] } });
}
/**
 * GET /brain/{tenant}/sessions/{id}/processes — read-only think-process
 * list of an arbitrary session. The WebSocket {@code process-list} resolves
 * against the <em>bound</em> session, so it cannot answer "what runs in that
 * other session?" — this is the picker's preview path.
 */
export async function listSessionProcesses(sessionId, includeTerminated = false) {
    const qs = includeTerminated ? '?includeTerminated=true' : '';
    return brainFetch('GET', `sessions/${encodeURIComponent(sessionId)}/processes${qs}`);
}
/**
 * GET .../processes/{name}/messages — one process's transcript, addressed by
 * process name inside the given session. Same reply shape as the WebSocket
 * {@code process-messages}, so a detail view can consume either source.
 */
export async function getSessionProcessMessages(sessionId, processName, limit) {
    const qs = limit && limit > 0 ? `?limit=${limit}` : '';
    return brainFetch('GET', `sessions/${encodeURIComponent(sessionId)}/processes/`
        + `${encodeURIComponent(processName)}/messages${qs}`);
}
//# sourceMappingURL=sessions.js.map